/**
 * RESPONSIBILITY: draw one event as a card, wherever a card is what is wanted.
 *
 * Home's scroller, the featured strip and a ministry page's band all show the same
 * event, so they show it the same way. `wide` is the only difference between them.
 *
 * The link says a word and never a position — reorder the list and this still points at
 * the same event, because the word comes from the row and not from where it sits.
 */
import type { EventRow } from '@app/sections';
import { Picture } from '../commons/Picture';
import { eventHref } from './event-href';
import { whenText, isVisible } from '../commons/when';
import { NotPublic } from '../commons/NotPublic';
import { EventTags } from './EventTags';

/**
 * IT MARKS ITSELF, and needs to be told nothing about who is looking.
 *
 * Every list on this site is filtered `canEdit || isVisible(e)`. So an event a visitor
 * cannot see reaching a card at all is proof that the person looking may edit it — there
 * is no other way it got here. Threading `canEdit` down to six call sites would have been
 * six chances to forget one, and a badge that appears on five pages out of six is worse
 * than none.
 */
export function EventCard({ e, wide = false }: { e: EventRow; wide?: boolean }) {
  const draft = !isVisible(e);

  return (
    <a className={`${wide ? 'card card-wide' : 'card'}${draft ? ' is-draft' : ''}`}
       href={eventHref(e)}>
      <Picture value={e.image} alt="" className="card-pic" />
      <div className="card-body">
        <p className="meta">{whenText(e)}</p>
        <h3>{e.title}</h3>
        {draft && <NotPublic />}
        {e.summary && <p>{e.summary}</p>}
        <EventTags e={e} />
      </div>
    </a>
  );
}
